// ── 모바일 담당매장 탭 — MobileView에서 분리 ─────────────────────────────────
// 매장카드 + 정산지급 + 상담자배분수입 + 기타수입 날짜순 통합 목록

import React, { useState } from 'react';
import { fmt } from './utils';
import { toDateString } from '../../../utils/date';
import { LoadingPlaceholder, EmptyState } from './CommonComponents';
import { StoreCard } from './StoreCard';
import { ConsultantChargeDetailModal } from '../../../components/settlement/ConsultantChargeDetailModal';

export function MobileStoresTab({
  clients, loading, records, shareIncome, others = [], consultantCharges = [],
  expandedId, setExpandedId,
  hasSelected, dragOverId,
  handleDragOver, handleDragLeave, handleDrop,
  handleStoreCardClick, handleUnlink,
  canEdit = false,       // 관리자만 true — StoreCard 편집 버튼 활성화
  onEditShareRate,       // (contractId, currentRate, defaultRate) => void
}) {
  const [chargeDetail, setChargeDetail] = useState(null);
  // 매장카드·정산지급·상담자배분수입·기타수입 날짜순 통합
  const mobileItems = [
    ...clients.map((c) => ({ kind: 'client',       data: c, date: c.latestDate || '' })),
    ...records.map((r) => ({ kind: 'settlement',   data: r, date: toDateString(r.settlement_date || '') })),
    ...consultantCharges.map((c) => ({ kind: 'consultant_charge', data: c, date: c.settlementDate || c.settlement_date || '' })),
    ...(shareIncome?.items || []).map((i) => ({ kind: 'share_income', data: i, date: i.date || '' })),
    ...others.map((o) => ({ kind: 'others',        data: o, date: o.date || '' })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

  if (loading) return <LoadingPlaceholder />;
  if (mobileItems.length === 0) return <EmptyState icon="🏪" text="담당 매장이 없습니다" />;

  return (
    <>
      {mobileItems.map((item) =>
        item.kind === 'client' ? (
          // ── 매장 카드 ──
          <StoreCard
            key={`client-${item.data.id}`}
            client={item.data}
            expanded={expandedId === item.data.id}
            onToggle={() => setExpandedId((p) => (p === item.data.id ? null : item.data.id))}
            isDragOver={dragOverId === item.data.id}
            hasSelected={hasSelected}
            onDragOver={(e) => handleDragOver(e, item.data.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, item.data.id)}
            onClick={() => handleStoreCardClick(item.data.id)}
            onUnlink={handleUnlink}
            canEdit={canEdit}
            onEditShareRate={onEditShareRate}
          />
        ) : item.kind === 'consultant_charge' ? (
          <div
            key={`consultant-charge-${item.data.id}`}
            style={{ borderRadius: 12, background: 'rgba(220,38,38,0.06)', border: '1.5px solid rgba(220,38,38,0.22)', padding: '14px 16px', marginBottom: 8, boxShadow: '0 2px 8px rgba(220,38,38,0.06)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '3px 9px', borderRadius: 12, whiteSpace: 'nowrap' }}>1:1급여</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{item.date}</span>
              </div>
              <span style={{ fontSize: 17, fontWeight: 800, color: '#dc2626', whiteSpace: 'nowrap' }}>-{fmt(item.data.chargeAmount ?? item.data.charge_amount ?? 0)}원</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <div style={{ fontSize: 13, color: 'var(--text-sub)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.data.label || `${item.data.consultantName || item.data.consultant_name || '1:1 직원'} 급여`}
              </div>
              <button
                type="button"
                onClick={() => setChargeDetail(item.data)}
                style={{ height: 32, minWidth: 82, border: '1px solid rgba(220,38,38,0.28)', borderRadius: 8, background: 'var(--bg-card)', color: '#dc2626', fontSize: 12, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 14px' }}
              >
                계산내역
              </button>
            </div>
          </div>
        ) : item.kind === 'share_income' ? (
          // ── 상담자 배분 수입 카드 ──
          // 내 상담자가 타 마케터 매장에서 계약을 잡아 발생한 수입
          <div
            key={`share-${item.data.contractId || item.data.clientId}`}
            style={{ borderRadius: 12, background: 'linear-gradient(135deg, #fce7f3 0%, #fdf2f8 100%)', border: '1.5px solid #f9a8d4', padding: '14px 16px', marginBottom: 8, boxShadow: '0 2px 8px rgba(190,24,93,0.08)' }}
          >
            {/* 1행: 뱃지 + 상담자명 + 금액 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, background: '#fce7f3', color: '#be185d', padding: '3px 9px', borderRadius: 12 }}>상담배분 수입</span>
                <span style={{ fontSize: 12, color: '#be185d', fontWeight: 600 }}>{item.data.consultantName}</span>
              </div>
              <span style={{ fontSize: 17, fontWeight: 800, color: '#be185d' }}>+{fmt(item.data.amount)}원</span>
            </div>
            {/* 2행: 어느 매장 계약인지 + 비율 분배 표기 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text-sub)' }}>{item.data.clientName}</span>
              <div style={{ display: 'flex', gap: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                <span>계약자 <strong style={{ color: 'var(--text-sub)' }}>{item.data.contractorRate ?? (100 - item.data.shareRate)}%</strong></span>
                <span>·</span>
                <span>내 몫 <strong style={{ color: '#be185d' }}>{item.data.shareRate}%</strong></span>
              </div>
            </div>
          </div>
        ) : item.kind === 'others' ? (
          // ── 기타 수입 카드 ──
          <div
            key={`others-${item.data.id}`}
            style={{ borderRadius: 12, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1.5px solid #fcd34d', padding: '14px 16px', marginBottom: 8, boxShadow: '0 2px 8px rgba(180,83,9,0.08)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#b45309', padding: '3px 9px', borderRadius: 12 }}>기타</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{item.data.date}</span>
              </div>
              {/* 음수(차감)이면 +를 붙이지 않음 */}
              <span style={{
                fontSize: 17,
                fontWeight: 800,
                color: Number(item.data.amount) > 0 ? '#059669' : Number(item.data.amount) < 0 ? '#dc2626' : '#b45309',
              }}>
                {item.data.amount > 0 ? '+' : ''}{fmt(item.data.amount)}원
              </span>
            </div>
            {/* 내용 + 비고 */}
            <div style={{ fontSize: 13, color: 'var(--text-sub)', marginTop: 6 }}>{item.data.description}</div>
            {item.data.note && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{item.data.note}</div>
            )}
          </div>
        ) : (
          // ── 정산지급 카드 — 정산서 합계에서는 지출(-)로 처리 ──
          <div
            key={`settlement-${item.data.id}`}
            style={{
              borderRadius: 12,
              background: 'rgba(220,38,38,0.06)',
              border: '1.5px solid rgba(220,38,38,0.22)', padding: '14px 16px', marginBottom: 8,
              boxShadow: '0 2px 8px rgba(220,38,38,0.06)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#dc2626', padding: '3px 9px', borderRadius: 12 }}>정산지급</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{toDateString(item.data.settlement_date)}</span>
              </div>
              <span style={{ fontSize: 17, fontWeight: 800, color: '#dc2626' }}>-{fmt(Math.abs(Number(item.data.amount) || 0))}원</span>
            </div>
            {item.data.note && (
              <div style={{ fontSize: 13, color: 'var(--text-sub)', marginTop: 8, borderTop: '1px solid rgba(220,38,38,0.18)', paddingTop: 8 }}>
                {item.data.note}
              </div>
            )}
          </div>
        )
      )}
      {chargeDetail && (
        <ConsultantChargeDetailModal
          charge={chargeDetail}
          summaryScope="settlement"
          onClose={() => setChargeDetail(null)}
        />
      )}
    </>
  );
}
